var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { chain, get, meanBy, round, sum, times } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../config';
import { CurrencyCode, TABLE_HIERARCHY_COLORS } from '../../../enum';
import { MULTISKILL_COLOR } from '../../annual/constants';
import { TableRow } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { FLAG } from '../../daily/constants';
import { QueueType } from '../../multiskill/types';
import { useReportAPI } from '../../report/selectors';
import { SEAT_EXCEEDED_COLOR, periodIndexToYearMonth, useSeatCapacity } from '../../seatCapacity';
import { DOUBLE_HYPHEN } from '../../wizard/workArrivalPattern/selectors';
import { metricTargetMap, pickPeriodSummaryAverage } from '../constants';
import { StyledFirstCell, StyledTableCell } from '../periodTable';
import { usePeriodReportChunkSize } from '../selectors';
import { INDENT_LEVELS, STICKIES, areAllOccupancyValues100Percent, getSeatsRequiredPeak } from './utils';
var YearCells = function (_a) {
  var yearValues = _a.yearValues,
    year = _a.year,
    totalValue = _a.totalValue,
    averageValue = _a.averageValue,
    isLastYear = _a.isLastYear,
    percentage = _a.percentage,
    isCurrency = _a.isCurrency,
    currency = _a.currency,
    averagePrecision = _a.averagePrecision,
    rowColor = _a.rowColor,
    cellColor = _a.cellColor,
    getCellColor = _a.getCellColor,
    isIncludeTotal = _a.isIncludeTotal,
    _b = _a.blankTotal,
    blankTotal = _b === void 0 ? false : _b,
    keyPrefix = _a.keyPrefix;
  return _jsxs(React.Fragment, {
    children: [yearValues.map(function (value, i) {
      var exceededColor = getCellColor === null || getCellColor === void 0 ? void 0 : getCellColor(i);
      return _jsx(StyledTableCell, {
        percentage: percentage,
        currency: isCurrency ? currency : undefined,
        value: value,
        decimal: averagePrecision,
        color: exceededColor !== null && exceededColor !== void 0 ? exceededColor : cellColor,
        useColorValue: !!exceededColor
      }, "".concat(keyPrefix, "-").concat(year, "-").concat(i));
    }), isIncludeTotal && _jsx(StyledTableCell, {
      color: rowColor,
      decimal: averagePrecision,
      currency: isCurrency ? currency : undefined,
      percentage: percentage,
      value: blankTotal ? undefined : totalValue
    }), _jsx(StyledTableCell, {
      color: rowColor,
      decimal: averagePrecision,
      colSpan: isIncludeTotal ? 1 : 2,
      highlightBorder: !isLastYear,
      percentage: percentage,
      currency: isCurrency ? currency : undefined,
      value: averageValue
    })]
  });
};
export var QUEUE_EXPANSION_KEY_SEPARATOR = '::';
var INDENT_ORDER = [INDENT_LEVELS.metric, INDENT_LEVELS.lob, INDENT_LEVELS.queue, INDENT_LEVELS.country, INDENT_LEVELS.location, INDENT_LEVELS.supportStaffLocation];
var getDeeperIndent = function (indent, depth) {
  var index = INDENT_ORDER.indexOf(indent !== null && indent !== void 0 ? indent : INDENT_LEVELS.queue);
  return INDENT_ORDER[index + depth];
};
export var getQueueExpansionKey = function (queueName, keyItem, role) {
  return [keyItem, role, queueName].filter(Boolean).join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
export var getLobExpansionKey = function (lobName, keyItem, role) {
  return [keyItem, role, 'lob', lobName || 'unassigned-lob'].filter(Boolean).join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
export var getQueueExpansionKeyForLob = function (lobName, queueName, keyItem, role, queueType) {
  return [getLobExpansionKey(lobName, keyItem, role), 'queue', queueType !== null && queueType !== void 0 ? queueType : '', queueName].join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
export var getCountryExpansionKey = function (parentExpansionKey, countryName) {
  return [parentExpansionKey, 'country', countryName].join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
var extractDataArray = function (queueData, role) {
  if (Array.isArray(queueData)) return queueData;
  if (queueData && typeof queueData === 'object' && !Array.isArray(queueData) && role) {
    return queueData[role];
  }
  return undefined;
};
var extractGeoDataArray = function (geoData, keyItem, role) {
  var _a;
  var rowValue = geoData[keyItem];
  var metricValues = role ? extractDataArray(rowValue, role) : extractDataArray(rowValue);
  return (_a = metricValues !== null && metricValues !== void 0 ? metricValues : keyItem === 'requiredAgents' ? geoData.requiredAgents : undefined) !== null && _a !== void 0 ? _a : [];
};
var hasGeoRoleData = function (geoData, keyItem, role) {
  if (!role) return true;
  return extractDataArray(geoData[keyItem], role) !== undefined;
};
var filterLocationsByRole = function (locations, keyItem, role) {
  return (locations !== null && locations !== void 0 ? locations : []).filter(function (location) {
    return hasGeoRoleData(location, keyItem, role);
  });
};
var filterCountriesByRole = function (countries, keyItem, role) {
  return (countries !== null && countries !== void 0 ? countries : []).filter(function (country) {
    return hasGeoRoleData(country, keyItem, role) || filterLocationsByRole(country.locations, keyItem, role).length;
  });
};
export var getQueueExpansionKeysForLob = function (lob, keyItem, role) {
  return lob.queues.filter(function (queue) {
    return queue.dataType !== QueueType.Multiskill;
  }).flatMap(function (queue) {
    var queueExpansionKey = getQueueExpansionKeyForLob(lob.lobName, queue.displayName, keyItem, role, queue.dataType);
    return __spreadArray([queueExpansionKey], filterCountriesByRole(queue.countries, keyItem, role).map(function (country) {
      return getCountryExpansionKey(queueExpansionKey, country.countryName);
    }), true);
  });
};
var getGeoAverage = function (values) {
  if (!values.length) return DOUBLE_HYPHEN;
  var average = meanBy(values, Number);
  return Number.isNaN(average) ? DOUBLE_HYPHEN : average;
};
var getGeoSum = function (values, averagePrecision) {
  var numericValues = values.map(Number).filter(function (value) {
    return !Number.isNaN(value);
  });
  if (!numericValues.length) return DOUBLE_HYPHEN;
  return round(sum(numericValues), averagePrecision);
};
var getGeoSummaryValue = function (keyItem, values, averagePrecision, summaryValue) {
  if (summaryValue === void 0) {
    summaryValue = 'avg';
  }
  if (summaryValue === 'total') return getGeoSum(values, averagePrecision);
  if (keyItem === 'forecastedDemand') return getGeoSum(values, averagePrecision);
  if (keyItem !== 'seatsRequired') return getGeoAverage(values);
  var max = chain(values).filter(function (value) {
    return value != null;
  }).map(Number).max().value();
  return max == null || Number.isNaN(max) ? DOUBLE_HYPHEN : max;
};
var getPeriodChunkValues = function (values, chunkSize, year, defaultValue) {
  return times(chunkSize).map(function (i) {
    var _a;
    return (_a = values[chunkSize * year + i]) !== null && _a !== void 0 ? _a : defaultValue;
  });
};
var GEO_SUM_TOTAL_KEYS = new Set(['supportStaff', 'agentsSalaries', 'supportStaffSalaries', 'hiringAhead', 'overtimeCosts', 'totalBudgets', 'costPerWork']);
var getGeoTotal = function (fallbackTotal, keyItem, values, averagePrecision) {
  if (!GEO_SUM_TOTAL_KEYS.has(keyItem)) {
    return Number.isNaN(fallbackTotal) ? DOUBLE_HYPHEN : fallbackTotal;
  }
  var total = chain(values).sumBy(Number).round(averagePrecision).value();
  return Number.isNaN(total) ? DOUBLE_HYPHEN : total;
};
var getQueueSummaryValue = function (_a) {
  var keyItem = _a.keyItem,
    yearValues = _a.yearValues,
    averagePrecision = _a.averagePrecision,
    apiChunk = _a.apiChunk,
    simpleMean = _a.simpleMean,
    total = _a.total,
    _b = _a.summaryValue,
    summaryValue = _b === void 0 ? 'avg' : _b;
  if (summaryValue === 'total') return Number.isNaN(total) ? DOUBLE_HYPHEN : total;
  if (keyItem === 'forecastedDemand' || keyItem === 'seatsRequired') {
    return getGeoSummaryValue(keyItem, yearValues, averagePrecision);
  }
  return pickPeriodSummaryAverage(keyItem, apiChunk, simpleMean);
};
var isQueueOccupancy100Percent = function (queue) {
  if (!queue.occupancy || !Array.isArray(queue.occupancy)) return false;
  return areAllOccupancyValues100Percent(queue.occupancy);
};
var isVisibleQueueRow = function (queue, keyItem, isMultiskill) {
  var _a;
  if (keyItem === 'productiveHours' && isQueueOccupancy100Percent(queue)) return false;
  if (keyItem === 'abandonmentRate' && !queue.isAbandonable) return false;
  if (keyItem === 'seatsRequired' && queue.group !== null && queue.dataType !== QueueType.Multiskill && !((_a = queue.seatsRequired) === null || _a === void 0 ? void 0 : _a.some(function (value) {
    return value != null;
  }))) {
    return false;
  }
  if (!isMultiskill && queue.dataType === QueueType.Multiskill) return false;
  return true;
};
export var getMultiskillSectionExpansionKey = function (keyItem, role) {
  return [keyItem, role, 'multiSkill'].filter(Boolean).join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
export var getMultiskillGroupExpansionKey = function (queueName, keyItem, role) {
  return [getMultiskillSectionExpansionKey(keyItem, role), 'group', queueName].filter(Boolean).join(QUEUE_EXPANSION_KEY_SEPARATOR);
};
var getQueueExpansionKeyForQueue = function (queue, keyItem, role, lobName) {
  var _a;
  if (queue.dataType === QueueType.Multiskill) {
    return getMultiskillGroupExpansionKey(queue.displayName, keyItem, role);
  }
  var queueLobName = (_a = queue.lobName) !== null && _a !== void 0 ? _a : lobName;
  return queueLobName === undefined ? getQueueExpansionKey(queue.displayName, keyItem, role) : getQueueExpansionKeyForLob(queueLobName, queue.displayName, keyItem, role, queue.dataType);
};
var getVisibleMultiskillGroupQueues = function (queues, keyItem) {
  return queues.filter(function (queue) {
    return queue.dataType === QueueType.Multiskill && isVisibleQueueRow(queue, keyItem, true);
  });
};
export var getMultiskillGroupExpansionKeys = function (queues, keyItem, role) {
  return getVisibleMultiskillGroupQueues(queues, keyItem).flatMap(function (queue) {
    var queueExpansionKey = getQueueExpansionKeyForQueue(queue, keyItem, role);
    return __spreadArray([queueExpansionKey], filterCountriesByRole(queue.countries, keyItem, role).map(function (country) {
      return getCountryExpansionKey(queueExpansionKey, country.countryName);
    }), true);
  });
};
export var getMultiskillExpansionKeys = function (queues, keyItem, role) {
  var groupKeys = getMultiskillGroupExpansionKeys(queues, keyItem, role);
  return groupKeys.length ? __spreadArray([getMultiskillSectionExpansionKey(keyItem, role)], groupKeys, true) : [];
};
var LocationRow = function (_a) {
  var location = _a.location,
    summary = _a.summary,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    role = _a.role,
    percentage = _a.percentage,
    isCurrency = _a.isCurrency,
    averagePrecision = _a.averagePrecision,
    isIncludeTotal = _a.isIncludeTotal,
    blankTotal = _a.blankTotal,
    rowColor = _a.rowColor,
    multiskillColor = _a.multiskillColor,
    multiskillGroup = _a.multiskillGroup,
    currency = _a.currency,
    locationIndent = _a.locationIndent,
    planId = _a.planId,
    summaryValue = _a.summaryValue;
  var locationValues = extractGeoDataArray(location, keyItem, role);
  var reportData = useReportAPI()[0].data;
  var getAvailable = useSeatCapacity().getAvailable;
  var isSeatsRequired = keyItem === 'seatsRequired';
  var getCellColor = isSeatsRequired && planId != null && (reportData === null || reportData === void 0 ? void 0 : reportData.startDate) && (reportData === null || reportData === void 0 ? void 0 : reportData.planType) ? function (absoluteIndex) {
    var seats = Number(locationValues[absoluteIndex]);
    if (!Number.isFinite(seats)) return undefined;
    var _a = periodIndexToYearMonth(reportData.startDate, reportData.planType, absoluteIndex),
      year = _a.year,
      month = _a.month;
    var available = getAvailable(planId, location.locationName, year, month);
    return available !== undefined && seats > available ? SEAT_EXCEEDED_COLOR : undefined;
  } : undefined;
  return _jsxs(TableRow, {
    children: [_jsx(StyledFirstCell, {
      stickySizes: STICKIES,
      color: multiskillGroup ? MULTISKILL_COLOR : theme.palette.background.default,
      "aria-label": locationIndent !== null && locationIndent !== void 0 ? locationIndent : INDENT_LEVELS.location,
      value: location.locationName
    }), summary[0].map(function (item, year, arr) {
      var yearValues = getPeriodChunkValues(locationValues, chunkSize, year, keyItem === 'supportStaff' ? 0 : undefined);
      var seatsRequiredPeak = keyItem === 'seatsRequired' ? getSeatsRequiredPeak(location, year * chunkSize, (year + 1) * chunkSize) : 0;
      return _jsx(YearCells, {
        yearValues: yearValues,
        year: year,
        totalValue: getGeoTotal(item.total, keyItem, yearValues, averagePrecision),
        averageValue: keyItem === 'seatsRequired' ? seatsRequiredPeak : getGeoSummaryValue(keyItem, yearValues, averagePrecision, summaryValue),
        isLastYear: year === arr.length - 1,
        percentage: percentage,
        isCurrency: isCurrency,
        currency: currency,
        averagePrecision: averagePrecision,
        rowColor: multiskillColor !== null && multiskillColor !== void 0 ? multiskillColor : rowColor,
        cellColor: multiskillColor !== null && multiskillColor !== void 0 ? multiskillColor : theme.palette.background.default,
        getCellColor: getCellColor ? function (i) {
          return getCellColor(chunkSize * year + i);
        } : undefined,
        isIncludeTotal: isIncludeTotal,
        blankTotal: blankTotal,
        keyPrefix: location.locationName
      }, "".concat(location.locationName, "-").concat(year));
    })]
  });
};
var CountryRow = function (_a) {
  var country = _a.country,
    expansionKey = _a.expansionKey,
    summary = _a.summary,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    role = _a.role,
    percentage = _a.percentage,
    isCurrency = _a.isCurrency,
    averagePrecision = _a.averagePrecision,
    isIncludeTotal = _a.isIncludeTotal,
    blankTotal = _a.blankTotal,
    rowColor = _a.rowColor,
    multiskillColor = _a.multiskillColor,
    multiskillGroup = _a.multiskillGroup,
    currency = _a.currency,
    countryIndent = _a.countryIndent,
    locationIndent = _a.locationIndent,
    planId = _a.planId,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    summaryValue = _a.summaryValue;
  var _b = useState(true),
    isCountryExpanded = _b[0],
    setIsCountryExpanded = _b[1];
  var isControlledCountry = expandedItems && Object.hasOwn(expandedItems, expansionKey);
  var expanded = isControlledCountry ? expandedItems[expansionKey] : isCountryExpanded;
  var countryValues = extractGeoDataArray(country, keyItem, role);
  var locations = filterLocationsByRole(country.locations, keyItem, role);
  var hasLocations = !!locations.length;
  var handleToggleCountry = isControlledCountry && onToggleItem ? function () {
    return onToggleItem(expansionKey);
  } : function () {
    return setIsCountryExpanded(function (value) {
      return !value;
    });
  };
  return _jsxs(React.Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(StyledFirstCell, __assign({
        stickySizes: STICKIES,
        color: multiskillGroup ? MULTISKILL_COLOR : undefined,
        "aria-label": countryIndent !== null && countryIndent !== void 0 ? countryIndent : INDENT_LEVELS.country
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: hasLocations ? handleToggleCountry : undefined,
          bold: true
        }, {
          children: country.countryName
        }))
      })), summary[0].map(function (item, year, arr) {
        var yearValues = getPeriodChunkValues(countryValues, chunkSize, year, keyItem === 'supportStaff' ? 0 : undefined);
        var seatsRequiredPeak = keyItem === 'seatsRequired' ? getSeatsRequiredPeak(country, year * chunkSize, (year + 1) * chunkSize) : 0;
        return _jsx(YearCells, {
          yearValues: yearValues,
          year: year,
          totalValue: getGeoTotal(item.total, keyItem, yearValues, averagePrecision),
          averageValue: keyItem === 'seatsRequired' ? seatsRequiredPeak : getGeoSummaryValue(keyItem, yearValues, averagePrecision, summaryValue),
          isLastYear: year === arr.length - 1,
          percentage: percentage,
          isCurrency: isCurrency,
          currency: currency,
          averagePrecision: averagePrecision,
          rowColor: multiskillColor !== null && multiskillColor !== void 0 ? multiskillColor : rowColor,
          cellColor: multiskillColor,
          isIncludeTotal: isIncludeTotal,
          blankTotal: blankTotal,
          keyPrefix: country.countryName
        }, "".concat(country.countryName, "-").concat(year));
      })]
    }), expanded && locations.map(function (location) {
      return _jsx(LocationRow, {
        location: location,
        summary: summary,
        chunkSize: chunkSize,
        keyItem: keyItem,
        role: role,
        percentage: percentage,
        isCurrency: isCurrency,
        averagePrecision: averagePrecision,
        isIncludeTotal: isIncludeTotal,
        blankTotal: blankTotal,
        rowColor: multiskillColor !== null && multiskillColor !== void 0 ? multiskillColor : rowColor,
        multiskillColor: multiskillColor,
        multiskillGroup: multiskillGroup,
        currency: currency,
        locationIndent: locationIndent,
        planId: planId,
        summaryValue: summaryValue
      }, "".concat(country.countryName, "-").concat(location.locationName));
    })]
  });
};
var QueueItemRow = function (_a) {
  var _b;
  var queue = _a.queue,
    lobName = _a.lobName,
    index = _a.index,
    summary = _a.summary,
    chunkSize = _a.chunkSize,
    keyItem = _a.keyItem,
    role = _a.role,
    percentage = _a.percentage,
    isCurrency = _a.isCurrency,
    averagePrecision = _a.averagePrecision,
    isIncludeTotal = _a.isIncludeTotal,
    blankTotal = _a.blankTotal,
    rowColor = _a.rowColor,
    currency = _a.currency,
    queueIndent = _a.queueIndent,
    countryIndent = _a.countryIndent,
    locationIndent = _a.locationIndent,
    isMultiskill = _a.isMultiskill,
    expandedQueues = _a.expandedQueues,
    expandedItems = _a.expandedItems,
    onToggleQueue = _a.onToggleQueue,
    onToggleItem = _a.onToggleItem,
    disableHierarchy = _a.disableHierarchy,
    planId = _a.planId,
    summaryValue = _a.summaryValue;
  if (!isVisibleQueueRow(queue, keyItem, isMultiskill)) return null;
  if (!summary[index]) return null;
  var isQueueMsk = queue.dataType === QueueType.Multiskill;
  var multiskillColor = isMultiskill && (isQueueMsk || queue.group !== null) ? MULTISKILL_COLOR : undefined;
  var queueExpansionKey = getQueueExpansionKeyForQueue(queue, keyItem, role, lobName);
  var isQueueExpanded = expandedItems ? (_b = expandedItems[queueExpansionKey]) !== null && _b !== void 0 ? _b : false : expandedQueues.includes(queueExpansionKey);
  var countries = filterCountriesByRole(queue.countries, keyItem, role);
  var hasCountries = !disableHierarchy && !!countries.length;
  return _jsxs(React.Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(StyledFirstCell, __assign({
        stickySizes: STICKIES,
        color: isMultiskill && isQueueMsk ? MULTISKILL_COLOR : undefined,
        "aria-label": queueIndent !== null && queueIndent !== void 0 ? queueIndent : INDENT_LEVELS.queue
      }, {
        children: _jsxs(ExpandableRowLabel, __assign({
          expanded: isQueueExpanded,
          onToggle: hasCountries ? function () {
            return onToggleItem ? onToggleItem(queueExpansionKey) : onToggleQueue(queueExpansionKey);
          } : undefined,
          bold: true
        }, {
          children: [queue.displayName, " ", queue.metricTarget === metricTargetMap[keyItem] && FLAG]
        }))
      })), summary[index].map(function (item, year, arr) {
        var _a, _b;
        var queueData = role ? get(queue, "".concat(keyItem, ".").concat(role)) : queue[keyItem];
        var dataArray = extractDataArray(queueData, role);
        var yearStart = chunkSize * year;
        var yearValues = (_a = dataArray === null || dataArray === void 0 ? void 0 : dataArray.slice(yearStart, yearStart + chunkSize)) !== null && _a !== void 0 ? _a : [];
        var yearValuesForCells = times(chunkSize).map(function (i) {
          return yearValues[i];
        });
        var seatsRequiredPeak = keyItem === 'seatsRequired' ? getSeatsRequiredPeak(queue, yearStart, yearStart + chunkSize) : 0;
        return _jsx(YearCells, {
          yearValues: yearValuesForCells,
          year: year,
          totalValue: Number.isNaN(item.total) ? DOUBLE_HYPHEN : item.total,
          averageValue: keyItem === 'seatsRequired' ? seatsRequiredPeak : getQueueSummaryValue({
            keyItem: keyItem,
            yearValues: yearValuesForCells,
            averagePrecision: averagePrecision,
            apiChunk: (_b = queue.summary[keyItem]) === null || _b === void 0 ? void 0 : _b[year],
            simpleMean: item.avg,
            total: item.total,
            summaryValue: summaryValue
          }),
          isLastYear: year === arr.length - 1,
          percentage: percentage,
          isCurrency: isCurrency,
          currency: currency,
          averagePrecision: averagePrecision,
          rowColor: multiskillColor !== null && multiskillColor !== void 0 ? multiskillColor : rowColor,
          cellColor: multiskillColor,
          isIncludeTotal: isIncludeTotal,
          blankTotal: blankTotal,
          keyPrefix: "queue-".concat(index)
        }, "queue-".concat(index, "-year-").concat(year));
      })]
    }), !disableHierarchy && isQueueExpanded && countries.map(function (country) {
      return _jsx(CountryRow, {
        country: country,
        expansionKey: getCountryExpansionKey(queueExpansionKey, country.countryName),
        summary: summary[index] ? [summary[index]] : [[{
          total: 0,
          avg: 0
        }]],
        chunkSize: chunkSize,
        keyItem: keyItem,
        role: role,
        percentage: percentage,
        isCurrency: isCurrency,
        averagePrecision: averagePrecision,
        isIncludeTotal: isIncludeTotal,
        blankTotal: blankTotal,
        rowColor: rowColor,
        multiskillColor: multiskillColor,
        multiskillGroup: isQueueMsk,
        currency: currency,
        countryIndent: countryIndent,
        locationIndent: locationIndent,
        planId: planId,
        summaryValue: summaryValue,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem
      }, "".concat(queue.displayName, "-").concat(country.countryName));
    })]
  });
};
var sumReportData = function (left, right) {
  if (left === void 0) {
    left = [];
  }
  if (right === void 0) {
    right = [];
  }
  var length = Math.max(left.length, right.length);
  return times(length).map(function (index) {
    var _a, _b;
    return Number((_a = left[index]) !== null && _a !== void 0 ? _a : 0) + Number((_b = right[index]) !== null && _b !== void 0 ? _b : 0);
  });
};
var mergeLocation = function (target, source, keyItem) {
  var _a;
  return __assign(__assign({}, target), (_a = {}, _a[keyItem] = sumReportData(extractGeoDataArray(target, keyItem), extractGeoDataArray(source, keyItem)), _a));
};
var mergeCountry = function (target, source, keyItem) {
  var _a;
  var locations = __spreadArray([], target.locations, true);
  source.locations.forEach(function (sourceLocation) {
    var targetIndex = locations.findIndex(function (location) {
      return sourceLocation.planLocationId !== undefined && location.planLocationId === sourceLocation.planLocationId || location.locationName === sourceLocation.locationName;
    });
    if (targetIndex >= 0) {
      locations[targetIndex] = mergeLocation(locations[targetIndex], sourceLocation, keyItem);
      return;
    }
    locations.push(sourceLocation);
  });
  return __assign(__assign({}, target), (_a = {}, _a[keyItem] = sumReportData(extractGeoDataArray(target, keyItem), extractGeoDataArray(source, keyItem)), _a.locations = locations, _a));
};
var aggregateCountries = function (queues, keyItem) {
  var countries = [];
  queues.forEach(function (queue) {
    var _a;
    (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.forEach(function (country) {
      var targetIndex = countries.findIndex(function (item) {
        return item.countryName === country.countryName;
      });
      if (targetIndex >= 0) {
        countries[targetIndex] = mergeCountry(countries[targetIndex], country, keyItem);
        return;
      }
      countries.push(country);
    });
  });
  return countries;
};
export var QueueRow = function (_a) {
  var data = _a.data,
    dataWithLob = _a.dataWithLob,
    _b = _a.averagePrecision,
    averagePrecision = _b === void 0 ? 0 : _b,
    _c = _a.isIncludeTotal,
    isIncludeTotal = _c === void 0 ? false : _c,
    _d = _a.blankTotal,
    blankTotal = _d === void 0 ? false : _d,
    keyItem = _a.keyItem,
    rowColor = _a.rowColor,
    role = _a.role,
    percentage = _a.percentage,
    isCurrency = _a.isCurrency,
    _e = _a.isMultiskill,
    isMultiskill = _e === void 0 ? false : _e,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem,
    _f = _a.disableHierarchy,
    disableHierarchy = _f === void 0 ? false : _f,
    _g = _a.countryOnly,
    countryOnly = _g === void 0 ? false : _g,
    providedCountries = _a.countries,
    lobIndent = _a.lobIndent,
    queueIndent = _a.queueIndent,
    countryIndent = _a.countryIndent,
    locationIndent = _a.locationIndent,
    planId = _a.planId,
    summaryValue = _a.summaryValue;
  var reportData = useReportAPI()[0].data;
  var chunkSize = usePeriodReportChunkSize();
  var t = useTranslation('report').t;
  var _h = reportData !== null && reportData !== void 0 ? reportData : {},
    _j = _h.periodLength,
    periodLength = _j === void 0 ? 0 : _j,
    _k = _h.currency,
    currency = _k === void 0 ? CurrencyCode.Dollar : _k;
  var summary = React.useMemo(function () {
    return data.map(function (queue) {
      var queueData = role ? get(queue, "".concat(keyItem, ".").concat(role)) : queue[keyItem];
      var dataArray = extractDataArray(queueData, role);
      return chain(periodLength).times().map(function (i) {
        var _a;
        return {
          value: Number((_a = dataArray === null || dataArray === void 0 ? void 0 : dataArray[i]) !== null && _a !== void 0 ? _a : 0),
          hasData: (dataArray === null || dataArray === void 0 ? void 0 : dataArray[i]) != null
        };
      }).chunk(chunkSize).map(function (arr) {
        return {
          total: chain(arr).sumBy('value').round(averagePrecision).value(),
          avg: percentage ? chain(arr).filter('hasData').meanBy('value').value() : chain(arr).filter('hasData').meanBy('value').round(averagePrecision).value()
        };
      }).value();
    });
  }, [averagePrecision, chunkSize, data, keyItem, percentage, periodLength, role]);
  var queueIndexByName = React.useMemo(function () {
    return new Map(data.map(function (queue, index) {
      return ["".concat(queue.displayName, "::").concat(queue.lobName), index];
    }));
  }, [data]);
  var countries = React.useMemo(function () {
    return filterCountriesByRole(providedCountries !== null && providedCountries !== void 0 ? providedCountries : aggregateCountries(data, keyItem), keyItem, role);
  }, [data, keyItem, providedCountries, role]);
  var countrySummary = React.useMemo(function () {
    return countries.map(function (country) {
      var countryValues = extractGeoDataArray(country, keyItem, role);
      return chain(periodLength).times().map(function (i) {
        var _a;
        return {
          value: Number((_a = countryValues[i]) !== null && _a !== void 0 ? _a : 0),
          hasData: countryValues[i] != null
        };
      }).chunk(chunkSize).map(function (arr) {
        return {
          total: chain(arr).sumBy('value').round(averagePrecision).value(),
          avg: percentage ? chain(arr).filter('hasData').meanBy('value').value() : chain(arr).filter('hasData').meanBy('value').round(averagePrecision).value()
        };
      }).value();
    });
  }, [averagePrecision, chunkSize, countries, keyItem, percentage, periodLength, role]);
  var _l = useState(function () {
      var keys = (dataWithLob === null || dataWithLob === void 0 ? void 0 : dataWithLob.length) ? dataWithLob.flatMap(function (lob) {
        return getQueueExpansionKeysForLob(lob, keyItem, role);
      }) : data.map(function (q) {
        return getQueueExpansionKey(q.displayName, keyItem, role);
      });
      var multiskillKeys = new Set(data.filter(function (q) {
        return q.dataType === QueueType.Multiskill;
      }).map(function (q) {
        return getQueueExpansionKeyForQueue(q, keyItem, role);
      }));
      return keys.filter(function (key) {
        return !multiskillKeys.has(key);
      });
    }),
    expandedQueues = _l[0],
    setExpandedQueues = _l[1];
  var _m = useState(function () {
      var _a;
      return (_a = dataWithLob === null || dataWithLob === void 0 ? void 0 : dataWithLob.map(function (lob) {
        return lob.lobName;
      })) !== null && _a !== void 0 ? _a : [];
    }),
    expandedLobs = _m[0],
    setExpandedLobs = _m[1];
  var toggleQueue = function (queueExpansionKey) {
    return setExpandedQueues(function (prev) {
      return prev.includes(queueExpansionKey) ? prev.filter(function (i) {
        return i !== queueExpansionKey;
      }) : __spreadArray(__spreadArray([], prev, true), [queueExpansionKey], false);
    });
  };
  var toggleLob = function (lobName) {
    return setExpandedLobs(function (prev) {
      return prev.includes(lobName) ? prev.filter(function (i) {
        return i !== lobName;
      }) : __spreadArray(__spreadArray([], prev, true), [lobName], false);
    });
  };
  var shouldUseMultiskillQueueFallback = isMultiskill && !(dataWithLob === null || dataWithLob === void 0 ? void 0 : dataWithLob.length);
  var multiskillGroupQueues = isMultiskill ? getVisibleMultiskillGroupQueues(data, keyItem) : [];
  var lobsWithoutGroups = React.useMemo(function () {
    return (dataWithLob !== null && dataWithLob !== void 0 ? dataWithLob : []).map(function (lob) {
      return __assign(__assign({}, lob), {
        queues: lob.queues.filter(function (queue) {
          return queue.dataType !== QueueType.Multiskill;
        })
      });
    }).filter(function (lob) {
      return lob.queues.length > 0;
    });
  }, [dataWithLob]);
  var _o = useState(false),
    localMultiskillSectionExpanded = _o[0],
    setLocalMultiskillSectionExpanded = _o[1];
  var multiskillSectionExpansionKey = getMultiskillSectionExpansionKey(keyItem, role);
  var isControlledMultiskillSection = expandedItems !== undefined && Object.hasOwn(expandedItems, multiskillSectionExpansionKey);
  var multiskillSectionExpanded = isControlledMultiskillSection ? expandedItems[multiskillSectionExpansionKey] : localMultiskillSectionExpanded;
  var handleToggleMultiskillSection = function () {
    if (isControlledMultiskillSection) {
      onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(multiskillSectionExpansionKey);
      return;
    }
    setLocalMultiskillSectionExpanded(function (value) {
      if (value) {
        multiskillGroupQueues.forEach(function (q) {
          var queueExpansionKey = getQueueExpansionKeyForQueue(q, keyItem, role);
          if (expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[queueExpansionKey]) {
            onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(queueExpansionKey);
          } else if (!expandedItems && expandedQueues.includes(queueExpansionKey)) {
            toggleQueue(queueExpansionKey);
          }
        });
      }
      return !value;
    });
  };
  var renderMultiskillGroupSection = function () {
    return multiskillGroupQueues.length > 0 && _jsxs(React.Fragment, {
      children: [_jsxs(TableRow, {
        children: [_jsx(StyledFirstCell, __assign({
          stickySizes: STICKIES,
          color: TABLE_HIERARCHY_COLORS.lightBlueGray,
          "aria-label": lobIndent !== null && lobIndent !== void 0 ? lobIndent : INDENT_LEVELS.lob
        }, {
          children: _jsx(ExpandableRowLabel, __assign({
            expanded: multiskillSectionExpanded,
            onToggle: handleToggleMultiskillSection,
            bold: true
          }, {
            children: t('period.multiskill')
          }))
        })), function () {
          var totalYears = periodLength / chunkSize;
          return times(totalYears).map(function (year) {
            return _jsxs(React.Fragment, {
              children: [times(chunkSize).map(function (i) {
                return _jsx(StyledTableCell, {
                  color: TABLE_HIERARCHY_COLORS.lightBlueGray
                }, "multiskill-header-year-".concat(year, "-cell-").concat(i));
              }), isIncludeTotal && _jsx(StyledTableCell, {
                color: TABLE_HIERARCHY_COLORS.lightBlueGray
              }), _jsx(StyledTableCell, {
                colSpan: isIncludeTotal ? 1 : 2,
                color: TABLE_HIERARCHY_COLORS.lightBlueGray,
                highlightBorder: year !== totalYears - 1
              })]
            }, "multiskill-header-year-".concat(year));
          });
        }()]
      }), multiskillSectionExpanded && multiskillGroupQueues.map(function (queue, groupIndex) {
        var index = data.indexOf(queue);
        return _jsx(QueueItemRow, {
          queue: queue,
          lobName: queue.lobName,
          index: index >= 0 ? index : groupIndex,
          summary: summary,
          chunkSize: chunkSize,
          keyItem: keyItem,
          role: role,
          percentage: percentage,
          isCurrency: isCurrency,
          averagePrecision: averagePrecision,
          isIncludeTotal: isIncludeTotal,
          blankTotal: blankTotal,
          rowColor: rowColor,
          currency: currency,
          queueIndent: queueIndent,
          countryIndent: getDeeperIndent(queueIndent, 1),
          locationIndent: getDeeperIndent(queueIndent, 2),
          isMultiskill: true,
          expandedQueues: expandedQueues,
          expandedItems: expandedItems,
          onToggleQueue: toggleQueue,
          onToggleItem: onToggleItem,
          disableHierarchy: disableHierarchy,
          planId: planId
        }, "multiskill-group-".concat(queue.displayName));
      })]
    }, "multiskill-section");
  };
  if (countryOnly) {
    return _jsxs(_Fragment, {
      children: [countries.map(function (country, index) {
        return _jsx(CountryRow, {
          country: country,
          expansionKey: getCountryExpansionKey(keyItem, country.countryName),
          summary: countrySummary[index] ? [countrySummary[index]] : [[{
            total: 0,
            avg: 0
          }]],
          chunkSize: chunkSize,
          keyItem: keyItem,
          role: role,
          percentage: percentage,
          isCurrency: isCurrency,
          averagePrecision: averagePrecision,
          isIncludeTotal: isIncludeTotal,
          blankTotal: blankTotal,
          rowColor: rowColor,
          currency: currency,
          countryIndent: countryIndent,
          locationIndent: locationIndent,
          planId: planId,
          summaryValue: summaryValue,
          expandedItems: expandedItems,
          onToggleItem: onToggleItem
        }, country.countryName);
      }), renderMultiskillGroupSection()]
    });
  }
  if (shouldUseMultiskillQueueFallback) {
    return _jsx(_Fragment, {
      children: data.map(function (queue, index) {
        return _jsx(QueueItemRow, {
          queue: queue,
          index: index,
          summary: summary,
          chunkSize: chunkSize,
          keyItem: keyItem,
          role: role,
          percentage: percentage,
          isCurrency: isCurrency,
          averagePrecision: averagePrecision,
          isIncludeTotal: isIncludeTotal,
          blankTotal: blankTotal,
          rowColor: rowColor,
          currency: currency,
          queueIndent: queueIndent,
          countryIndent: countryIndent,
          locationIndent: locationIndent,
          isMultiskill: isMultiskill,
          expandedQueues: expandedQueues,
          expandedItems: expandedItems,
          onToggleQueue: toggleQueue,
          onToggleItem: onToggleItem,
          disableHierarchy: disableHierarchy,
          planId: planId,
          summaryValue: summaryValue
        }, queue.displayName);
      })
    });
  }
  return _jsxs(_Fragment, {
    children: [lobsWithoutGroups.map(function (lob) {
      var lobExpansionKey = getLobExpansionKey(lob.lobName, keyItem, role);
      var isControlledLob = expandedItems && Object.hasOwn(expandedItems, lobExpansionKey);
      var isLobExpanded = isControlledLob ? expandedItems[lobExpansionKey] : expandedLobs.includes(lob.lobName);
      var displayLobName = lob.lobName || t('period.unassignedLob');
      var visibleQueues = lob.queues.filter(function (queue) {
        return isVisibleQueueRow(queue, keyItem, isMultiskill);
      });
      var hasVisibleQueues = visibleQueues.length > 0;
      if (!hasVisibleQueues) return null;
      var handleToggleLob = isControlledLob && onToggleItem ? function () {
        return onToggleItem(lobExpansionKey);
      } : function () {
        return toggleLob(lob.lobName);
      };
      if (keyItem === 'productiveHours' && !hasVisibleQueues) return null;
      return _jsxs(React.Fragment, {
        children: [_jsxs(TableRow, {
          children: [_jsx(StyledFirstCell, __assign({
            stickySizes: STICKIES,
            color: theme.palette.grey[400],
            "aria-label": lobIndent !== null && lobIndent !== void 0 ? lobIndent : INDENT_LEVELS.lob
          }, {
            children: _jsx(ExpandableRowLabel, __assign({
              expanded: isLobExpanded,
              onToggle: hasVisibleQueues ? handleToggleLob : undefined
            }, {
              children: displayLobName
            }))
          })), function () {
            var totalYears = periodLength / chunkSize;
            return times(totalYears).map(function (year) {
              return _jsxs(React.Fragment, {
                children: [times(chunkSize).map(function (i) {
                  return _jsx(StyledTableCell, {
                    color: theme.palette.grey[400]
                  }, "lob-header-year-".concat(year, "-cell-").concat(i));
                }), isIncludeTotal && _jsx(StyledTableCell, {
                  color: theme.palette.grey[400]
                }), _jsx(StyledTableCell, {
                  colSpan: isIncludeTotal ? 1 : 2,
                  color: theme.palette.grey[400],
                  highlightBorder: year !== totalYears - 1
                })]
              }, "lob-header-year-".concat(year));
            });
          }()]
        }), isLobExpanded && visibleQueues.map(function (queue) {
          var _a;
          var queueKey = "".concat(queue.displayName, "::").concat(lob.lobName);
          return _jsx(QueueItemRow, {
            queue: queue,
            keyItem: keyItem,
            index: (_a = queueIndexByName.get(queueKey)) !== null && _a !== void 0 ? _a : -1,
            lobName: lob.lobName,
            averagePrecision: averagePrecision,
            blankTotal: blankTotal,
            chunkSize: chunkSize,
            countryIndent: countryIndent,
            currency: currency,
            disableHierarchy: disableHierarchy,
            expandedItems: expandedItems,
            expandedQueues: expandedQueues,
            isCurrency: isCurrency,
            isIncludeTotal: isIncludeTotal,
            isMultiskill: isMultiskill,
            locationIndent: locationIndent,
            onToggleItem: onToggleItem,
            onToggleQueue: toggleQueue,
            percentage: percentage,
            planId: planId,
            queueIndent: queueIndent,
            role: role,
            rowColor: rowColor,
            summary: summary,
            summaryValue: summaryValue
          }, queueKey);
        })]
      }, lob.lobName || 'unassigned-lob');
    }), multiskillGroupQueues.length > 0 && renderMultiskillGroupSection()]
  });
};